import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { AuthenticateApiService } from './authenticate-api.service';
import { PresenceService } from './presence.service';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

// Single owner of session-expiry state: ticks the countdown, warns ~5 minutes
// before expiry, and performs/shares the refresh-token exchange so the
// warning UI and the HTTP interceptor never race each other into issuing
// duplicate refresh calls.
@Injectable({
    providedIn: 'root',
})
export class SessionService {

    private countdownSubject = new BehaviorSubject<string>('');
    countdown$: Observable<string> = this.countdownSubject.asObservable();

    private warningSubject = new BehaviorSubject<boolean>(false);
    warning$: Observable<boolean> = this.warningSubject.asObservable();

    private intervalId: any = null;
    private warningShown = false;
    private refreshInFlight$: Observable<any> | null = null;

    constructor(
        private ngZone: NgZone,
        private router: Router,
        private notification: NzNotificationService,
        private tokenService: TokenService,
        private authenticateApiService: AuthenticateApiService,
        private presenceService: PresenceService,
    ) { }

    start(): void {
        if (this.intervalId) return;

        this.tick();
        this.ngZone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => this.ngZone.run(() => this.tick()), 1000);
        });
    }

    stop(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.warningShown = false;
        this.warningSubject.next(false);
        this.countdownSubject.next('');
    }

    // Refreshes the access token, sharing one in-flight request between the
    // warning UI's "Extend" click and any concurrent 401 retries.
    refresh(): Observable<any> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const refreshToken = this.tokenService.getRefreshToken();
        if (!refreshToken) {
            return throwError(() => new Error('No refresh token available'));
        }

        const request$ = this.authenticateApiService.Refresh(refreshToken).pipe(
            tap(response => {
                this.tokenService.setAccessToken(response.accessToken);
                this.tokenService.setExpiryTime(response.expiresOnUtc);
                this.tokenService.setRefreshToken(response.refreshToken);
                this.warningShown = false;
                this.warningSubject.next(false);
            }),
            shareReplay(1),
            finalize(() => {
                this.refreshInFlight$ = null;
            }),
        );

        this.refreshInFlight$ = request$;
        return request$;
    }

    extend(): void {
        this.refresh().subscribe({
            next: () => this.notification.success('Session extended', 'You\'ll stay signed in.'),
            error: () => this.forceLogout(),
        });
    }

    private tick(): void {
        const expiryIso = this.tokenService.getExpiryTime(null);
        if (!expiryIso) {
            this.countdownSubject.next('');
            return;
        }

        const expiryMs = this.parseIsoToMs(expiryIso);
        if (isNaN(expiryMs)) {
            this.countdownSubject.next('Invalid expiry');
            return;
        }

        const diff = expiryMs - Date.now();
        if (diff <= 0) {
            this.countdownSubject.next('Token expired');
            this.stop();
            this.forceLogout();
            return;
        }

        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        this.countdownSubject.next(`${mins}m ${secs}s`);

        if (diff <= WARNING_THRESHOLD_MS && !this.warningShown) {
            this.warningShown = true;
            this.warningSubject.next(true);
        }
    }

    private forceLogout(): void {
        this.tokenService.clearSession();
        this.presenceService.stop();
        this.warningSubject.next(false);

        const currentUrl = this.router.url || "/";
        const currentPath = currentUrl.split('?')[0].split('#')[0];
        // Same auth-page exclusion as ApiErrorInterceptor.redirectToLogin —
        // don't turn the login/error pages themselves into the returnUrl.
        const onAuthPage = currentPath === '' || currentPath === '/'
            || currentPath.startsWith('/login')
            || currentPath.startsWith('/404')
            || currentPath.startsWith('/500');

        if (onAuthPage) {
            this.router.navigateByUrl('/login');
        } else {
            this.router.navigate(["/login"], { queryParams: { returnUrl: currentUrl } });
        }
    }

    private parseIsoToMs(iso: string): number {
        let s = iso?.trim();
        if (!s) return NaN;
        if (!/[zZ]|[+\-]\d{2}:\d{2}$/.test(s)) s = s + 'Z';
        return Date.parse(s);
    }
}
